import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControl,
  FormControlLabel, InputLabel, LinearProgress, Link, MenuItem, Select, Stack,
  TextField, Typography,
} from '@mui/material';
import Panel from '../crt/Panel';
import SectionTitle from '../crt/SectionTitle';
import PatreonCatalogReference from './PatreonCatalogReference';
import PatreonPreviewPicker from './PatreonPreviewPicker';
import PatreonPostsManager from './PatreonPostsManager';
import {
  patreonPostAPI, uploadsAPI, PatreonInventory, PatreonPostJob, StartPatreonPostReq,
  PatreonCatalogFacet, PatreonEditSeed,
} from '../../services/api';

interface Props {
  site: 'spoutlore' | 'mallbrats';
  // Optional: a Patreon post can be created standalone (no website/Megaphone
  // episode). When present it's recorded against the WP post for job history.
  wpPostId?: number;
  defaultTitle?: string;
  defaultBody?: string;
  youtubeWatchUrl?: string;   // prefill for video-as-link
  seasonNumber?: number;
}

type PostType = 'audio' | 'video' | 'text' | 'image' | 'link' | 'poll';
const POST_TYPES: PostType[] = ['audio', 'video', 'text', 'image', 'link', 'poll'];

const dollars = (cents: number | null) =>
  cents == null ? '' : cents === 0 ? 'Free' : `$${(cents / 100).toFixed(0)}`;

const PatreonPublishPanel: React.FC<Props> = ({
  site, wpPostId, defaultTitle, defaultBody, youtubeWatchUrl, seasonNumber,
}) => {
  const [health, setHealth] = useState<{ ok: boolean; authed_as?: string; reason?: string } | null>(null);
  const [inv, setInv] = useState<PatreonInventory | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // form
  const [postType, setPostType] = useState<PostType>('audio');
  const [title, setTitle] = useState(defaultTitle || '');
  const [body, setBody] = useState(defaultBody || '');
  const [mediaSource, setMediaSource] = useState<'upload' | 'link' | 'none'>('upload');
  const [linkUrl, setLinkUrl] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [collection, setCollection] = useState('');
  const [podcast, setPodcast] = useState('');
  const [tierIds, setTierIds] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [realCollections, setRealCollections] = useState<PatreonCatalogFacet[]>([]);
  const [realPodcasts, setRealPodcasts] = useState<PatreonCatalogFacet[]>([]);
  const [previewDur, setPreviewDur] = useState(300);
  const [previewStart, setPreviewStart] = useState(0);
  // podcast episode metadata (shown when a podcast feed is selected)
  const [episodeType, setEpisodeType] = useState('full');
  const [episodeNumber, setEpisodeNumber] = useState('');
  const [epSeason, setEpSeason] = useState('');
  const [explicit, setExplicit] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [autoPublish, setAutoPublish] = useState(false);

  // run
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [job, setJob] = useState<PatreonPostJob | null>(null);
  const [retryErr, setRetryErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  // edit mode — the form edits an existing Patreon post instead of creating one
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editMsg, setEditMsg] = useState<string | null>(null);
  const [editAudioUrl, setEditAudioUrl] = useState<string | null>(null);
  const [editMediaId, setEditMediaId] = useState<string | null>(null);
  const [previewChanged, setPreviewChanged] = useState(false);
  const [editLoadingLive, setEditLoadingLive] = useState(false);
  const [editBodyField, setEditBodyField] = useState<string>('content');

  const enterEditMode = (seed: PatreonEditSeed) => {
    if (!seed.patreon_post_id) return;
    setEditingPostId(seed.patreon_post_id);
    if (seed.post_type) setPostType(seed.post_type as PostType);
    setTitle(seed.title || '');
    setBody(seed.body || '');
    setTags(seed.tags || []);
    if (seed.tier_ids && seed.tier_ids.length) setTierIds(seed.tier_ids);
    setCollection(seed.collection || '');
    setPodcast(seed.podcast || '');
    if (typeof seed.is_public === 'boolean') setIsPublic(seed.is_public);
    setJob(null); setSubmitErr(null); setEditMsg(null);
    setEditAudioUrl(null); setEditMediaId(null); setPreviewChanged(false);
    setPreviewStart(0); setPreviewDur(300); setEditBodyField('content');
    setEpisodeType('full'); setEpisodeNumber(''); setEpSeason(''); setExplicit(false);
    // Seed above is a fast first paint from our (possibly stale) record. Overwrite
    // it with Patreon's LIVE state — that's the source of truth for editing.
    setEditLoadingLive(true);
    patreonPostAPI.liveCatalogPost(seed.patreon_post_id)
      .then((live) => {
        if (live.title != null) setTitle(live.title);
        if (live.body != null) setBody(live.body || '');
        if (live.body_field) setEditBodyField(live.body_field);
        if (live.tags) setTags(live.tags);
        if (live.episode_type) setEpisodeType(live.episode_type);
        setEpisodeNumber(live.episode_number != null ? String(live.episode_number) : '');
        setEpSeason(live.season != null ? String(live.season) : '');
        setExplicit(!!live.is_explicit);
        if (live.tier_ids && live.tier_ids.length) setTierIds(live.tier_ids);
        setCollection(live.collection || '');
        setPodcast(live.podcast || '');
        if (typeof live.is_public === 'boolean') setIsPublic(live.is_public);
      })
      .catch(() => { /* keep the seed values */ })
      .finally(() => setEditLoadingLive(false));
    // for audio posts, re-source the original audio so the In/Out waveform works
    if (seed.post_type === 'audio') {
      patreonPostAPI.postAudio(seed.patreon_post_id)
        .then((a) => {
          setEditAudioUrl(a.audio_url); setEditMediaId(a.media_id);
          // seed the waveform In/Out to Patreon's saved preview
          if (a.preview_start_s != null) setPreviewStart(a.preview_start_s);
          if (a.preview_duration_s != null) setPreviewDur(a.preview_duration_s);
        })
        .catch(() => { /* no staged audio → numeric only */ });
    }
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* noop */ }
  };

  const exitEditMode = () => {
    setEditingPostId(null); setEditMsg(null);
    setEditAudioUrl(null); setEditMediaId(null); setPreviewChanged(false);
  };

  // ── load health + inventory ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [h, i] = await Promise.all([
          patreonPostAPI.health().catch((e) => ({ ok: false, reason: String(e) })),
          patreonPostAPI.inventory().catch(() => null),
        ]);
        if (!alive) return;
        setHealth(h as any);
        if (i) setInv(i);
      } catch (e: any) {
        if (alive) setLoadErr(e?.message || String(e));
      }
      // catalog-backed pickers (tags / real collections / podcast feeds) —
      // independent + best-effort so a failure never blocks the form.
      try {
        const [tg, co, pd] = await Promise.all([
          patreonPostAPI.catalogTags().catch(() => ({ tags: [] })),
          patreonPostAPI.catalogCollections().catch(() => ({ collections: [] })),
          patreonPostAPI.catalogPodcasts().catch(() => ({ podcasts: [] })),
        ]);
        if (!alive) return;
        setTagOptions(tg.tags || []);
        setRealCollections(co.collections || []);
        setRealPodcasts(pd.podcasts || []);
      } catch { /* best-effort */ }
    })();
    return () => { alive = false; };
  }, []);

  // ── default tier gating when post type / inventory changes ──
  useEffect(() => {
    if (!inv) return;
    if (editingPostId) return;  // editing: keep the post's own tiers, don't reset
    const def = postType === 'video' ? inv.defaults.video : inv.defaults.audio;
    setTierIds(def);
    // video default → media is a YouTube link; audio → file upload
    if (postType === 'video') {
      setMediaSource(youtubeWatchUrl ? 'link' : 'upload');
      if (youtubeWatchUrl) setLinkUrl(youtubeWatchUrl);
    } else if (postType === 'audio') {
      setMediaSource('upload');
    } else {
      setMediaSource('none');
    }
  }, [postType, inv, youtubeWatchUrl, editingPostId]);

  const isAV = postType === 'audio' || postType === 'video';
  const tierLabel = useMemo(() => {
    if (!inv) return '';
    const sel = inv.access_rules.filter((r) => tierIds.includes(r.id));
    return sel.map((r) => r.title + (r.amount_cents ? ` (${dollars(r.amount_cents)})` : '')).join(', ');
  }, [inv, tierIds]);

  // ── poll a running job ──
  useEffect(() => {
    if (!job || ['done', 'error'].includes(job.status)) return;
    const id = setInterval(async () => {
      try {
        const j = await patreonPostAPI.getJob(job.job_id);
        setJob(j);
        if (['done', 'error'].includes(j.status)) clearInterval(id);
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(id);
  }, [job]);

  const canSubmit = editingPostId ? !!title.trim() : (!!title.trim() && (!isAV
    || (mediaSource === 'link' && !!linkUrl.trim())
    || (mediaSource === 'upload' && (!!mediaFile))
    || mediaSource === 'none'));

  const podcastMeta = () => (podcast ? {
    episode_type: episodeType,
    episode_number: episodeNumber.trim() ? Number(episodeNumber) : null,
    season: epSeason.trim() ? Number(epSeason) : null,
    is_explicit: explicit,
  } : undefined);

  const submit = async () => {
    setSubmitErr(null);
    setSubmitting(true);
    // edit mode — PATCH the existing post (no media upload, no new draft)
    if (editingPostId) {
      try {
        await patreonPostAPI.editCatalogPost(editingPostId, {
          title: title.trim(),
          // never blank the body: omit it if empty (guards against a failed load)
          body: body.trim() ? body : undefined,
          body_field: editBodyField,
          tier_ids: tierIds, tags,
          collection: collection || undefined, podcast: podcast || undefined,
          is_public: isPublic, podcast_meta: podcastMeta(),
        });
        // if the In/Out window was adjusted, push the preview too
        if (previewChanged && postType === 'audio') {
          await patreonPostAPI.setPreview(editingPostId, {
            start_s: previewStart, duration_s: previewDur, media_id: editMediaId,
          }).catch((e) => { throw new Error('post saved, but preview failed: ' + (e?.message || e)); });
        }
        setEditMsg('Saved changes to Patreon.');
        setPreviewChanged(false);
      } catch (e: any) {
        setSubmitErr(e?.response?.data?.detail || e?.message || String(e));
      } finally { setSubmitting(false); }
      return;
    }
    try {
      let mediaGetUrl: string | undefined;
      let mediaFileName: string | undefined;
      if (isAV && mediaSource === 'upload' && mediaFile) {
        setUploadPct(0);
        const kind = postType === 'audio' ? 'audio' : 'video';
        // Files over the 5GB single-PUT limit (and large videos generally) must
        // go multipart — a single presigned PUT 400s past 5GB. Use multipart for
        // anything over 100MB; small audio stays on the simple path.
        if (mediaFile.size > 100 * 1024 * 1024) {
          const up = await uploadsAPI.uploadToS3Multipart(
            site, kind, mediaFile, (p) => setUploadPct(p));
          mediaGetUrl = up.get_url;
        } else {
          const presign = await uploadsAPI.presign({
            site, kind, filename: mediaFile.name,
            content_type: mediaFile.type || 'application/octet-stream',
          });
          await uploadsAPI.uploadToS3(
            presign.put_url, mediaFile, mediaFile.type || 'application/octet-stream',
            (p) => setUploadPct(p));
          mediaGetUrl = presign.get_url;
        }
        mediaFileName = mediaFile.name;
        setUploadPct(null);
      }
      const req: StartPatreonPostReq = {
        post_type: postType, site, wp_post_id: wpPostId,
        title: title.trim(), body,
        media_source: isAV ? mediaSource : 'none',
        media_get_url: mediaGetUrl, media_file_name: mediaFileName,
        link_url: mediaSource === 'link' ? linkUrl.trim() : undefined,
        collection: collection || undefined,
        podcast: podcast || undefined,
        tier_ids: tierIds,
        is_public: isPublic,
        auto_publish: autoPublish,
        preview: isAV ? { duration_s: previewDur, start_s: previewStart } : undefined,
        tags,
        tag_context: { site, season_number: seasonNumber },
        podcast_meta: podcastMeta(),
      };
      const j = await patreonPostAPI.startPost(req);
      setJob(j);
    } catch (e: any) {
      setSubmitErr(e?.response?.data?.detail || e?.message || String(e));
      setUploadPct(null);
    } finally {
      setSubmitting(false);
    }
  };

  const retry = async () => {
    if (!job) return;
    setRetryErr(null);
    try { setJob(await patreonPostAPI.retryJob(job.job_id)); }
    catch (e: any) { setRetryErr(e?.response?.data?.detail || e?.message || 'retry failed'); }
  };

  return (
    <Panel label="STEP 5 :: POST TO PATREON (OPTIONAL)">
      <SectionTitle>{`> CREATE A PATREON POST — AUDIO / VIDEO / TEXT, TIER-GATED_`}</SectionTitle>
      <Box sx={{ p: 2 }}>
        {/* auth banner */}
        {health && !health.ok && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Patreon session not active.</strong>{' '}
            {health.reason || 'No session cookie.'}<br />
            Re-capture it in <strong>Patreon → Session</strong>: paste a fresh{' '}
            <code>session_id</code> (DevTools › Application › Cookies › patreon.com ›
            <code>session_id</code>) and the server verifies it live.
          </Alert>
        )}
        {health?.ok && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Authed as <strong>{health.authed_as}</strong>.
          </Alert>
        )}
        {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}

        {/* internal record — partial/failed drafts to resume + live posts to edit */}
        <PatreonPostsManager onEdit={enterEditMode} />

        {/* result / job state */}
        {job && (
          <Alert
            severity={job.status === 'done' ? 'success' : job.status === 'error' ? 'error' : 'info'}
            sx={{ mb: 2 }}
            action={job.status === 'error'
              ? <Button color="inherit" size="small" onClick={retry}>RETRY</Button>
              : undefined}
          >
            <div><strong>Job {job.status}</strong> ({job.progress_pct}%)</div>
            {!['done', 'error'].includes(job.status) && (
              <LinearProgress variant="determinate" value={job.progress_pct} sx={{ mt: 1 }} />
            )}
            {job.note && (
              <div style={{ marginTop: 6, opacity: 0.85 }}>{job.note}</div>
            )}
            {job.patreon_url && (
              <div style={{ marginTop: 6 }}>
                <Link href={job.patreon_url} target="_blank" rel="noreferrer">
                  {job.patreon_url}
                </Link>{' '}
                {!job.auto_publish && job.status === 'done' && (
                  <span>— created as a <strong>draft</strong>; review &amp; publish on Patreon.</span>
                )}
              </div>
            )}
            {job.error && <div style={{ marginTop: 6, fontFamily: 'monospace' }}>{job.error}</div>}
            {retryErr && <div style={{ marginTop: 6, color: '#d32f2f' }}>Retry failed: {retryErr}</div>}
          </Alert>
        )}

        {!job && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' } }}>
          <Stack spacing={2} sx={{ flex: 2, minWidth: 0, width: '100%' }}>
            {/* edit-mode banner */}
            {editingPostId && (
              <Alert severity="info" action={
                <Button color="inherit" size="small" onClick={exitEditMode}>Cancel edit</Button>
              }>
                ✏️ <strong>Editing an existing Patreon post</strong> — change the fields below and
                hit <em>Save changes to Patreon</em>. (Media stays as-is.)
                {editLoadingLive && <em> · loading current state from Patreon…</em>}
              </Alert>
            )}
            {editMsg && <Alert severity="success">{editMsg}</Alert>}
            {/* type */}
            <FormControl size="small" sx={{ maxWidth: 220 }}>
              <InputLabel>Post type</InputLabel>
              <Select label="Post type" value={postType}
                onChange={(e) => setPostType(e.target.value as PostType)}>
                {POST_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField label="Title" size="small" value={title} fullWidth
              onChange={(e) => setTitle(e.target.value)} />
            <TextField label="Body (HTML or plain text)" size="small" value={body}
              fullWidth multiline minRows={3}
              onChange={(e) => setBody(e.target.value)} />

            {/* media — hidden in edit mode (the post's media stays as-is) */}
            {isAV && !editingPostId && (
              <Box sx={{ border: '1px dashed', borderColor: 'divider', p: 1.5, borderRadius: 1 }}>
                <FormControl size="small" sx={{ maxWidth: 260, mb: 1 }}>
                  <InputLabel>Media source</InputLabel>
                  <Select label="Media source" value={mediaSource}
                    onChange={(e) => setMediaSource(e.target.value as any)}>
                    <MenuItem value="upload">Upload {postType} file</MenuItem>
                    {postType === 'video' && <MenuItem value="link">Link (YouTube / external URL)</MenuItem>}
                    <MenuItem value="none">No media (metadata only)</MenuItem>
                  </Select>
                </FormControl>
                {mediaSource === 'link' && (
                  <TextField label="Video URL" size="small" fullWidth value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://youtu.be/…" />
                )}
                {mediaSource === 'upload' && (
                  <Box>
                    <Button variant="outlined" component="label" size="small">
                      {mediaFile ? `📎 ${mediaFile.name}` : `Choose ${postType} file`}
                      <input hidden type="file"
                        accept={postType === 'audio' ? 'audio/*' : 'video/*'}
                        onChange={(e) => setMediaFile(e.target.files?.[0] || null)} />
                    </Button>
                    {uploadPct != null && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption">Staging to S3… {uploadPct}%</Typography>
                        <LinearProgress variant="determinate" value={uploadPct} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* tiers */}
            {inv && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Tiers / access (audio → all paid · video → $10+) ·{' '}
                  <strong>{tierIds.length} selected</strong> — click to toggle:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {inv.access_rules
                    .filter((r) => r.rule_type === 'tier' || r.rule_type === 'patrons')
                    .map((r) => {
                      const on = tierIds.includes(r.id);
                      return (
                        <Chip key={r.id} size="small"
                          label={`${on ? '✓ ' : ''}${r.title}${r.amount_cents ? ' ' + dollars(r.amount_cents) : ''}`}
                          onClick={() => setTierIds(on
                            ? tierIds.filter((x) => x !== r.id)
                            : [...tierIds, r.id])}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: on ? 700 : 400,
                            opacity: on ? 1 : 0.5,
                            border: on ? '1px solid' : '1px dashed',
                            borderColor: on ? '#964c5e' : 'text.disabled',
                            bgcolor: on ? '#964c5e' : 'transparent',
                            color: on ? '#fff' : 'text.secondary',
                            '&:hover': { bgcolor: on ? '#673441' : 'rgba(150,76,94,0.15)' },
                          }} />
                      );
                    })}
                </Box>
              </Box>
            )}

            {/* collection + podcast — distinct facets (podcast = RSS feed) */}
            {inv && (
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Collection</InputLabel>
                  <Select label="Collection" value={collection}
                    onChange={(e) => setCollection(e.target.value)}>
                    <MenuItem value=""><em>none</em></MenuItem>
                    {(realCollections.length ? realCollections : inv.collections).map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.title}{'posts' in c ? ` (${(c as PatreonCatalogFacet).posts})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Podcast feed (RSS)</InputLabel>
                  <Select label="Podcast feed (RSS)" value={podcast}
                    onChange={(e) => setPodcast(e.target.value)}>
                    <MenuItem value=""><em>not a podcast</em></MenuItem>
                    {(realPodcasts.length ? realPodcasts : inv.collections).map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.title}{'posts' in c ? ` (${(c as PatreonCatalogFacet).posts})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}

            {/* podcast episode metadata (RSS) — when a podcast feed is selected */}
            {podcast && (
              <Box sx={{ border: '1px dashed', borderColor: 'divider', p: 1.5, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Podcast episode details (RSS)
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                  <TextField label="Episode #" size="small" type="number" sx={{ maxWidth: 110 }}
                    value={episodeNumber} onChange={(e) => setEpisodeNumber(e.target.value)} />
                  <TextField label="Season" size="small" type="number" sx={{ maxWidth: 110 }}
                    value={epSeason} onChange={(e) => setEpSeason(e.target.value)} />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Episode type</InputLabel>
                    <Select label="Episode type" value={episodeType}
                      onChange={(e) => setEpisodeType(e.target.value)}>
                      <MenuItem value="full">Full</MenuItem>
                      <MenuItem value="trailer">Trailer</MenuItem>
                      <MenuItem value="bonus">Bonus</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel control={
                    <Checkbox checked={explicit} onChange={(e) => setExplicit(e.target.checked)} />
                  } label="Explicit" />
                </Stack>
              </Box>
            )}

            {/* tags — multiselect/autocomplete from real catalog tags */}
            <Autocomplete
              multiple freeSolo size="small" options={tagOptions} value={tags}
              onChange={(_, v) => setTags(v as string[])}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))}
              renderInput={(params) => (
                <TextField {...params} label="Tags" placeholder="Pick or type a tag…" />
              )}
            />

            {/* public preview In/Out picker — audio uses a decoded waveform; video
                uses a <video> scrubber (a video container can't be Web-Audio decoded) */}
            {isAV && (editingPostId ? (
              editAudioUrl ? (
                <PatreonPreviewPicker
                  url={editAudioUrl} startS={previewStart} durationS={previewDur}
                  onChange={(s, d) => { setPreviewStart(s); setPreviewDur(d); setPreviewChanged(true); }} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Original audio isn't available to scrub here — preview left unchanged.
                </Typography>
              )
            ) : (
              mediaSource === 'upload' && mediaFile ? (
                <PatreonPreviewPicker
                  file={mediaFile} startS={previewStart} durationS={previewDur}
                  onChange={(s, d) => { setPreviewStart(s); setPreviewDur(d); }} />
              ) : (
                <TextField label="Public preview length (seconds)" size="small" type="number"
                  value={previewDur} sx={{ maxWidth: 260 }}
                  onChange={(e) => setPreviewDur(Number(e.target.value) || 0)} />
              )
            ))}

            <Stack direction="row" spacing={3} flexWrap="wrap">
              <FormControlLabel control={
                <Checkbox checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
              } label="Public post (default: patrons only)" />
              {!editingPostId && (
                <FormControlLabel control={
                  <Checkbox checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)} />
                } label="Publish immediately (default: leave as draft to review)" />
              )}
            </Stack>

            {submitErr && <Alert severity="error">{submitErr}</Alert>}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Gating: <strong>{tierLabel || '—'}</strong>
                {!editingPostId && <>{' · '}{autoPublish ? 'will publish' : 'will be left as a draft'}</>}
              </Typography>
              <Button variant="contained" disabled={!canSubmit || submitting || (health ? !health.ok : false)}
                onClick={submit}
                startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
                {editingPostId
                  ? (submitting ? 'Saving…' : 'Save changes to Patreon')
                  : (submitting ? 'Submitting…' : 'Create Patreon post')}
              </Button>
            </Box>
          </Stack>
          {/* reference pane — browse past posts beside the draft */}
          <Box sx={{
            flex: 1, width: '100%', minWidth: { md: 300 },
            position: { md: 'sticky' as const }, top: 8,
            maxHeight: { md: '82vh' }, display: 'flex', flexDirection: 'column',
            border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25,
          }}>
            <PatreonCatalogReference onEdit={enterEditMode} />
          </Box>
          </Box>
        )}
      </Box>
    </Panel>
  );
};

export default PatreonPublishPanel;
